import type { ApiMessage } from '@xtreed/academy-shared';
import { api } from './api';

export interface DashboardStats {
  stats: {
    totalUsers: number;
    activeCourses: number;
    coursesInDevelopment: number;
    totalEnrollments: number;
    completionRate: string;
    totalRevenue: string;
    monthlyRevenue: string;
    totalSales: number;
    monthlySales: number;
    averageTicket: string;
    refundRate: string;
    chargebackRate: string;
  };
  enrollmentData: Array<{
    name: string;
    matriculas: number;
    conclusoes: number;
  }>;
  revenueData: Array<{
    name: string;
    receita: number;
    vendas: number;
    reembolsos: number;
  }>;
  coursesByCategory: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  topCourses: Array<{
    name: string;
    students: number;
    rating: number;
    trend: 'up' | 'down' | 'neutral';
  }>;
  recentPurchases: Array<{
    id: string;
    userName: string;
    userEmail: string;
    userAvatar: string | null;
    offerName: string;
    amount: number;
    amountFormatted: string;
    paymentMethod: string | null;
    paymentStatus: string;
    paymentGateway: string | null;
    paidAt: string | null;
    createdAt: string;
  }>;
  paymentMethodBreakdown: Array<{
    method: string | null;
    label: string;
    amount: number;
    count: number;
    percentage: number;
    color: string;
  }>;
}

export interface StudentMetrics {
  total: number;
  newToday: number;
  newThisWeek: number;
  newThisMonth: number;
  dau: number;
  wau: number;
  mau: number;
  verifiedEmails: number;
  withEnrollments: number;
  withCompletions: number;
  enrollmentRate: string;
  completionRate: string;
  newStudentsPerDay: Array<{ date: string; alunos: number }>;
  enrollmentsByStatus: {
    active: number;
    expired: number;
    cancelled: number;
  };
}

export interface LessonMetrics {
  totalLessons: number;
  totalStarted: number;
  totalCompleted: number;
  completionRate: string;
  watchedThisWeek: number;
  watchedThisMonth: number;
  totalWatchSessions: number;
  averageProgress: string;
  watchPerDay: Array<{
    date: string;
    assistidas: number;
    concluidas: number;
  }>;
  topLessons: Array<{
    title: string;
    module: string;
    course: string;
    duration: number;
    views: number;
    completions: number;
    completionRate: string;
  }>;
}

export interface CoursePerformance {
  id: string;
  title: string;
  thumbnail: string | null;
  createdAt: string;
  totalEnrollments: number;
  activeEnrollments: number;
  completedEnrollments: number;
  modules: number;
  avgProgress: string;
  completionRate: string;
}

export interface RecentActivity {
  recentEnrollments: Array<{
    id: string;
    userName: string;
    userAvatar: string | null;
    courseName: string;
    date: string;
    type: 'enrollment';
  }>;
  recentCompletions: Array<{
    id: string;
    userName: string;
    userAvatar: string | null;
    courseName: string;
    date: string | null;
    type: 'completion';
  }>;
  recentLessons: Array<{
    id: string;
    userName: string;
    userAvatar: string | null;
    lessonName: string;
    date: string | null;
    type: 'lesson';
  }>;
}

export interface ComprehensiveDashboard extends DashboardStats {
  studentMetrics: StudentMetrics;
  lessonMetrics: LessonMetrics;
  coursePerformance: CoursePerformance[];
  recentActivity: RecentActivity;
}

class AnalyticsService {
  async getDashboardStats(): Promise<DashboardStats> {
    const { data } = await api.get('/analytics/dashboard');
    return data?.data || data;
  }

  async getComprehensiveDashboard(): Promise<ComprehensiveDashboard> {
    const { data } = await api.get('/analytics/comprehensive');
    return data?.data || data;
  }

  async deleteDraftCourses(): Promise<{ deleted: number } & ApiMessage> {
    const { data } = await api.delete('/products/drafts/all');
    return data?.data || data;
  }
}

export const analyticsService = new AnalyticsService();
